import { useParams, Link } from 'react-router-dom';
import { videos } from '@/mocks/videos';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const videoId = parseInt(id || '1', 10);
  const video = videos.find((v) => v.id === videoId);

  if (!video) {
    return (
      <main className="pt-16 min-h-screen">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">视频未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您查找的视频不存在或已被删除。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.id !== videoId && v.categorySlug === video.categorySlug)
    .slice(0, 3);

  const schemaJson = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: video.title,
      description: video.description,
      datePublished: video.releaseDate,
      author: {
        '@type': 'Organization',
        name: '桔子视频',
      },
      publisher: {
        '@type': 'Organization',
        name: '桔子视频',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://juzi.video/article/${video.id}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://juzi.video/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: video.category,
          item: `https://juzi.video/category/${video.categorySlug}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: video.title,
          item: `https://juzi.video/article/${video.id}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `${video.title}在哪里可以在线观看？`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `您可以在桔子视频的${video.platform}专区免费在线观看${video.title}，支持高清画质，流畅不缓冲。`,
          },
        },
        {
          '@type': 'Question',
          name: `${video.title}总共多少集？`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `${video.title}是一部精彩的电视剧作品，每集时长约${video.duration}，具体集数请访问桔子视频查看完整播放列表。`,
          },
        },
        {
          '@type': 'Question',
          name: `${video.title}的评分是多少？`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `${video.title}在桔子视频上的评分为${video.rating}分，深受观众喜爱和好评。`,
          },
        },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={`${video.title} | 高清在线观看 - 桔子视频`}
        description={video.description}
        keywords={`${video.title},${video.tags.join(',')},桔子视频,在线观看,高清全集`}
        canonicalUrl={`https://juzi.video/article/${video.id}`}
        ogType="article"
        schemaJson={schemaJson}
      />

      <main className="pt-16">
        <div className="max-w-[1200px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: video.category, href: `/category/${video.categorySlug}` },
              { label: video.title },
            ]}
          />

          <article className="px-4 md:px-6 pb-14">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="relative aspect-video rounded-2xl overflow-hidden mb-6">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors duration-200">
                        <i className="ri-play-fill text-background-50 text-3xl ml-1"></i>
                      </div>
                      <div>
                        <span className="text-background-50 text-lg font-medium block">点击播放</span>
                        <span className="text-background-300 text-sm">{video.duration} / 超清</span>
                      </div>
                    </div>
                  </div>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {video.title}
                </h1>

                <div className="flex items-center gap-4 mb-6 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                      <i className="ri-star-fill text-background-50 text-xs"></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-800">{video.rating}</span>
                  </div>
                  <span className="text-foreground-400">|</span>
                  <span className="text-sm text-foreground-500">{video.releaseDate} 上线</span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-sm text-foreground-500">
                    {video.views > 10000 ? `${(video.views / 10000).toFixed(0)}万次播放` : `${video.views}次播放`}
                  </span>
                  <span className="text-foreground-400">|</span>
                  <Link
                    to={`/category/${video.categorySlug}`}
                    className="text-sm text-primary-500 font-medium hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
                  >
                    {video.platform}
                  </Link>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="prose prose-sm max-w-none">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">剧集介绍</h2>
                  <div className="text-foreground-700 leading-relaxed text-sm space-y-4">
                    {video.content.split('\n').filter(Boolean).map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <div className="bg-background-100 rounded-2xl p-5 mb-6">
                    <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">剧集信息</h3>
                    <div className="flex flex-col gap-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-foreground-500">平台</span>
                        <span className="text-sm font-medium text-foreground-800">{video.platform}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-foreground-500">评分</span>
                        <span className="text-sm font-medium text-primary-500">{video.rating}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-foreground-500">时长</span>
                        <span className="text-sm font-medium text-foreground-800">{video.duration}/集</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-foreground-500">上线日期</span>
                        <span className="text-sm font-medium text-foreground-800">{video.releaseDate}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-foreground-500">播放量</span>
                        <span className="text-sm font-medium text-foreground-800">
                          {video.views > 10000 ? `${(video.views / 10000).toFixed(0)}万` : video.views}
                        </span>
                      </div>
                    </div>
                  </div>

                  {relatedVideos.length > 0 && (
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">相关推荐</h3>
                      <div className="flex flex-col gap-3">
                        {relatedVideos.map((rv) => (
                          <Link
                            key={rv.id}
                            to={`/article/${rv.id}`}
                            className="group flex gap-3 bg-background-50 rounded-xl p-3 transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                          >
                            <div className="w-28 h-16 rounded-lg overflow-hidden flex-shrink-0">
                              <img
                                src={rv.thumbnail}
                                alt={rv.title}
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                                {rv.title}
                              </h4>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-primary-500 font-medium">{rv.rating}</span>
                                <span className="text-xs text-foreground-500">{rv.platform}</span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </article>
        </div>
      </main>
    </>
  );
}